import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const emptyForm = {
  title: "",
  value: "",
  valueDisplay: "",
  userId: "",
  expectedCloseDate: "",
  notes: "",
};

// converte "1.234,56" → 1234.56 (mesmo padrão do TransactionModal)
const parseBRL = (str) => {
  const clean = str.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? "" : n;
};

const formatBRL = (digits) => {
  const n = parseInt(digits.replace(/\D/g, ""), 10) || 0;
  return (n / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const OpportunityModal = ({ open, onClose, opportunity, initialContact }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  // seleção de contato (só quando não há contato fixo nem edição)
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [searching, setSearching] = useState(false);

  const fixedContact = opportunity?.contact || initialContact || null;

  useEffect(() => {
    if (!open) return;

    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    loadUsers();

    if (opportunity) {
      const rawValue = opportunity.value || 0;
      const digits = Math.round(Number(rawValue) * 100).toString();
      setForm({
        title: opportunity.title || "",
        value: rawValue || "",
        valueDisplay: rawValue ? formatBRL(digits) : "",
        userId: opportunity.userId || "",
        expectedCloseDate: opportunity.expectedCloseDate || "",
        notes: opportunity.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
    setSelectedContact(null);
    setContactSearch("");
  }, [open, opportunity]);

  useEffect(() => {
    if (!open || fixedContact || contactSearch.length < 2) return;
    setSearching(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", {
          params: { searchParam: contactSearch },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [contactSearch, open, fixedContact]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleValueChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      setForm((prev) => ({ ...prev, value: "", valueDisplay: "" }));
      return;
    }
    const display = formatBRL(digits);
    setForm((prev) => ({ ...prev, value: parseBRL(display), valueDisplay: display }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Informe o título do negócio.");
      return;
    }
    const contactId = fixedContact?.id || selectedContact?.id;
    if (!opportunity && !contactId) {
      toast.error("Selecione o contato do negócio.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        value: form.value === "" ? null : form.value,
        userId: form.userId === "" ? null : form.userId,
        expectedCloseDate: form.expectedCloseDate || null,
        notes: form.notes || null,
      };

      let saved;
      if (opportunity) {
        const { data } = await api.put(`/opportunities/${opportunity.id}`, payload);
        saved = data;
        toast.success("Negócio atualizado.");
      } else {
        const { data } = await api.post("/opportunities", {
          ...payload,
          contactId,
        });
        saved = data;
        toast.success("Negócio criado.");
      }
      onClose(saved);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {opportunity ? "Editar negócio" : "Novo negócio"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {fixedContact ? (
              <TextField
                label="Contato"
                value={fixedContact.name || ""}
                fullWidth
                variant="outlined"
                size="small"
                disabled
              />
            ) : (
              <Autocomplete
                options={contactOptions}
                loading={searching}
                value={selectedContact}
                getOptionLabel={(option) => option.name || ""}
                onChange={(e, newValue) => setSelectedContact(newValue)}
                onInputChange={(e, newInputValue) => setContactSearch(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Contato"
                    variant="outlined"
                    size="small"
                    autoFocus
                    placeholder="Digite para buscar..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Título"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Ex: Pacote trimestral"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Valor (R$)"
              value={form.valueDisplay}
              onChange={handleValueChange}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="0,00"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Previsão de fechamento"
              type="date"
              value={form.expectedCloseDate}
              onChange={handleChange("expectedCloseDate")}
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Responsável</InputLabel>
              <Select
                value={form.userId}
                onChange={handleChange("userId")}
                label="Responsável"
              >
                <MenuItem value="">
                  <em>Sem responsável</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observações"
              value={form.notes}
              onChange={handleChange("notes")}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpportunityModal;
